// Short-lived effects: puffs, sparks, hearts, sleepy z's and the like.

import { clockNow } from './clock.js';
import { env, rand } from './core.js';
import { HEART, ZED } from './props.js';

const list = [];

// Removes every effect at once (the gallery starts each recording from a clean screen).
export function clear() { list.length = 0; }

// How many effects are alive (a host that paces its frames by what is on screen asks).
export const count = () => list.length;

// p: { kind, x, y, vx, vy, g, life, size, color }
export function emit(p) { list.push({ vx: 0, vy: 0, g: 0, size: 1, ...p, born: clockNow() }); }

export function puff(x, y, s, color = '#EDE7DF') {
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    emit({ kind: 'square', x, y, vx: Math.cos(a) * 9 * s, vy: Math.sin(a) * 6 * s - 2.5 * s, life: 420, size: 0.9, color, shrink: true, fade: 0.75 });
  }
}

export function burst(x, y, s, colors, n = 12, speed = 10, g = 0, life = 600) {
  for (let i = 0; i < n; i++) {
    const a = rand(0, Math.PI * 2), v = rand(0.4, 1) * speed * s;
    emit({ kind: 'square', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, g: g * s, life: rand(0.7, 1.1) * life, size: rand(0.5, 0.9), color: colors[i % colors.length], shrink: true, fade: 1 });
  }
}

export const star = (x, y, life = 650) => emit({ kind: 'star', x, y, life });
export const heart = (x, y, s) => emit({ kind: 'sprite', art: HEART, x, y, vx: rand(-1.5, 1.5) * s, vy: -7 * s, life: 1100, scale: Math.max(1, Math.round(s * 0.6)) });
export const zed = (x, y, s) => emit({ kind: 'sprite', art: ZED, x, y, vx: 2.2 * s, vy: -4.5 * s, life: 1800, scale: Math.max(1, Math.round(s * 0.55)), wobble: true });

export function sparks(x, y, s, dirX, dirY) {   // a firework rocket's trail
  for (let i = 0; i < 3; i++) {
    emit({ kind: 'square', x: x + rand(-1, 1) * s, y: y + rand(-1, 1) * s, vx: dirX * rand(4, 9) * s + rand(-2, 2) * s, vy: dirY * rand(4, 9) * s + rand(-2, 2) * s,
      g: 3 * s, life: rand(260, 480), size: rand(0.4, 0.7), color: ['#FFF4C2', '#FFD15C', '#FF9B3D', '#FFFFFF'][i % 4], shrink: true, fade: 1 });
  }
}

export function draw(now) {
  const ctx = env.ctx;
  for (let i = list.length - 1; i >= 0; i--) {
    const p = list[i], t = (now - p.born) / 1000;
    // Every effect is drawn at least once, fully: one emitted each frame for just that frame (an
    // arrow in flight, a beam) would otherwise be gone before the next frame is drawn.
    let age = (now - p.born) / p.life;
    if (age >= 1 && p.drawn) { list.splice(i, 1); continue; }
    if (!p.drawn) { p.drawn = true; age = Math.min(age, 0.5); }
    const x = p.x + p.vx * t + (p.wobble ? Math.sin(t * 5) * 4 : 0), y = p.y + p.vy * t + 0.5 * p.g * t * t;
    ctx.globalAlpha = (p.fade ?? 0.95) * (age > 0.6 ? (1 - age) / 0.4 : 1);
    if (p.kind === 'square') {
      const s = Math.max(1, Math.round(env.S * p.size * (p.shrink ? 1 - age * 0.7 : 1)));
      ctx.fillStyle = p.color;
      ctx.fillRect(Math.round(x - s / 2), Math.round(y - s / 2), s, s);
    } else if (p.kind === 'star') {
      const u = Math.max(1, Math.round(env.S * 0.5)), arm = Math.round(Math.sin(age * Math.PI) * 2);
      const cx = Math.round(x), cy = Math.round(y);
      ctx.fillStyle = '#FFFBEA';
      ctx.fillRect(cx, cy, u, u);
      for (let j = 1; j <= arm; j++) {
        ctx.fillRect(cx + j * u, cy, u, u); ctx.fillRect(cx - j * u, cy, u, u);
        ctx.fillRect(cx, cy + j * u, u, u); ctx.fillRect(cx, cy - j * u, u, u);
      }
    } else if (p.kind === 'sprite') {
      for (const [c, r, col] of p.art) { ctx.fillStyle = col; ctx.fillRect(Math.round(x + c * p.scale), Math.round(y + r * p.scale), p.scale, p.scale); }
    }
    ctx.globalAlpha = 1;
  }
}
